import { RequestBuilder } from "../requestBuilder";
import {
  UsageBreakdownParamsSchema,
  UsageBreakdownResponseSchema,
  UsageDebitsParamsSchema,
  UsageDebitsResponseSchema,
  UsageItemsParamsSchema,
  UsageItemsResponseSchema,
  UsageSummaryParamsSchema,
  UsageSummaryResponseSchema,
} from "./usageClient.types";

// The organization rides the tenancy header rather than the path, so every
// config here is organization-scoped and none of them names an org in its URL.

const filterQuery = (params: { filterUser?: string; filterProject?: string; filterType?: string }) => {
  const query: Record<string, string> = {};
  if (params.filterUser) query.filter_user = params.filterUser;
  if (params.filterProject) query.filter_project = params.filterProject;
  if (params.filterType) query.filter_type = params.filterType;
  return query;
};

// GET /orgs/usage/summary - headline credit numbers and the forecast
export const UsageSummaryRequestConfig = new RequestBuilder({
  paramsSchema: UsageSummaryParamsSchema,
  path: () => "/orgs/usage/summary",
  responseSchema: UsageSummaryResponseSchema,
})
  .withQueryParams((params) => ({ start: params.start, end: params.end }))
  .withIncludeOrganizationId();

// GET /orgs/usage/breakdown - the range's spend grouped on one or two axes
export const UsageBreakdownRequestConfig = new RequestBuilder({
  paramsSchema: UsageBreakdownParamsSchema,
  path: () => "/orgs/usage/breakdown",
  responseSchema: UsageBreakdownResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = {
      start: params.start,
      end: params.end,
      dimension: params.dimension,
      ...filterQuery(params),
    };
    if (params.subDimension !== undefined) query.sub_dimension = params.subDimension;
    return query;
  })
  .withIncludeOrganizationId();

// GET /orgs/usage/debits - the rows behind one cell of a breakdown
export const UsageDebitsRequestConfig = new RequestBuilder({
  paramsSchema: UsageDebitsParamsSchema,
  path: () => "/orgs/usage/debits",
  responseSchema: UsageDebitsResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = {
      start: params.start,
      end: params.end,
      dimension: params.dimension,
      key: params.key,
      ...filterQuery(params),
    };
    if (params.sort !== undefined) query.sort = params.sort;
    if (params.order !== undefined) query.order = params.order;
    // The pair narrows to one cell of a two-axis grid; the server reads neither
    // without the other, so they are written together.
    if (params.subDimension !== undefined && params.subKey !== undefined) {
      query.sub_dimension = params.subDimension;
      query.sub_key = params.subKey;
    }
    if (params.afterId !== undefined) query.after_id = params.afterId;
    if (params.limit !== undefined) query.limit = String(params.limit);
    return query;
  })
  .withIncludeOrganizationId();

// GET /orgs/usage/items - the range's spend rolled up per chat, session, and asset
export const UsageItemsRequestConfig = new RequestBuilder({
  paramsSchema: UsageItemsParamsSchema,
  path: () => "/orgs/usage/items",
  responseSchema: UsageItemsResponseSchema,
})
  .withQueryParams((params) => {
    const query: Record<string, string> = { start: params.start, end: params.end, ...filterQuery(params) };
    if (params.sort !== undefined) query.sort = params.sort;
    if (params.order !== undefined) query.order = params.order;
    if (params.afterId !== undefined) query.after_id = params.afterId;
    if (params.limit !== undefined) query.limit = String(params.limit);
    return query;
  })
  .withIncludeOrganizationId();
